import { Box, CircularProgress, Divider, Typography } from '@mui/material'
import { IconCheck, IconX } from '@tabler/icons-react'
import Papa from 'papaparse'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { Dialog } from '~/components'
import { Button } from '~/components/Form/Button'
import { DragAndDrop } from '~/components/Form/DragAndDrop'
import { Form } from '~/components/Form/Form'
import { Input } from '~/components/Form/Input'
import { UpdateInvoiceToExternalNegotiationModel, useUpdateInvoicesToExternalNegotiationMutation } from '~/graphql/types'

export type MassInvoiceToExternalNegotiationDialogProps = {
  isVisible: boolean
  toggleModal: () => void
}

export const MassInvoiceToExternalNegotiationDialog: React.FC<MassInvoiceToExternalNegotiationDialogProps> = ({ isVisible, toggleModal }) => {

  const methods = useForm({
    mode: 'onChange',

  })
  const [resultList, setResultList] = useState<UpdateInvoiceToExternalNegotiationModel | null>(null)

  const [updateInvoices, { loading }] = useUpdateInvoicesToExternalNegotiationMutation({
    onCompleted(data) {
      setResultList(data.updateInvoicesToExternalNegotiation)
    },
    onError() {
      toast.error('Erro ao atualizar faturas.')
    },
  })

  const handleUpdateInvoices = async () => {
    const annotation = methods.getValues('annotation') || ''
    const idsFromText: string[] = annotation
      .split('\n')
      .map((id: string) => id.trim())
      .filter((id: string) => id.length > 0)

    const files = methods.getValues('energyBill') as File[] | undefined
    const idsFromFile: string[] = await (async () => {
      if (files && files.length > 0) {
        const text = await files[0].text()
        const parsed = Papa.parse<string[]>(text, { skipEmptyLines: true })
        return (parsed.data as string[][])
          .map(row => Array.isArray(row) ? row[0] : row)
          .map(id => (id ?? '').trim())
          .filter(id => id.length > 0)
      }
      return []
    })()

    const invoiceIds = Array.from(new Set([...idsFromText, ...idsFromFile]))

    updateInvoices({
      variables: {
        invoiceIds
      }
    })
  }

  const handleReset = () => {
    setResultList(null)
    methods.reset()
  }

  const annotation = methods.watch('annotation') || ''
  const files = methods.watch('energyBill') as File[] | undefined
  const isActivateDisabled = !annotation.trim() && (!files || files.length === 0)

  return (
    <Dialog
      isVisible={isVisible}
      title='Atualizar faturas para negociação externa'
      onClose={toggleModal}

    >
      <Divider />
      <Box sx={{ display: 'flex', flexFlow: 'column', gap: '2rem', padding: '2rem 0' }}>
        {!resultList ? (
          <>
            <Typography>Anexe um arquivo em CSV ou preencha o campo abaixo com o ID de cada fatura a ser atualizada:</Typography>
            <FormProvider {...methods}>
              <Form>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                  <DragAndDrop
                    canResend
                    name='energyBill'
                    $variant='secondary'
                    disabled={false}
                    label='Anexar arquivo'
                    fileDescription='CSV'
                    fileName={undefined}
                    fileUrl={''}
                    $row={true}
                  />
                  <Input
                    multiline
                    minRows={5}
                    name='annotation'
                    placeholder='Insira os IDs das faturas a serem atualizadas, um por linha.'
                  />
                </Box>
              </Form>
            </FormProvider>
            <Typography>Deseja atualizar as faturas informadas para o status <strong>negociação externa?</strong></Typography>
          </>
        )
          : (
            <>
              <Typography>Veja abaixo quais faturas foram atualizadas e quais falharam, com as respectivas justificativas.</Typography>
              <Box sx={{ maxHeight: 250, overflowY: 'auto', mb: 2 }}>
                {resultList.updated?.map((id) => (
                  <Box key={id} sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: '24px', py: 1, borderBottom: '1px solid #eee' }}>
                    <Typography variant='body2' color='#999999'>{id}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography variant='body2'>Atualizada</Typography>
                      <IconCheck color='#00A86B' />
                    </Box>
                    <Typography variant='body2' sx={{ flex: 1, textAlign: 'end' }}>-</Typography>
                  </Box>
                ))}
                {resultList.notUpdated.map((item) => (
                  <Box key={item.id} sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: '24px', py: 1, borderBottom: '1px solid #eee' }}>
                    <Typography variant='body2' color='#999999'>{item.id}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography variant='body2'>Erro</Typography>
                      <IconX color='#EF4444' />
                    </Box>
                    <Typography variant='body2' sx={{ flex: 1, textAlign: 'end' }}>{item.error || '-'}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

      </Box>
      <Divider />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '2rem' }}>
        {!resultList ? (
          <>
            <Button disabled={loading} onClick={toggleModal} color='secondary'>Cancelar</Button>
            <Button
              endIcon={loading ? <CircularProgress size={24} color='inherit' /> : null}
              onClick={handleUpdateInvoices}
              disabled={loading || isActivateDisabled}
            >
              Atualizar faturas
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleReset} color='secondary'>Atualizar mais faturas</Button>
            <Button onClick={
              () => {
                toggleModal()
                handleReset()
              }} variant='contained'>Concluir</Button>
          </>
        )}
      </Box>
    </Dialog>
  )
}
